import { lazy, Suspense, useEffect, useState, type ReactNode } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "./lib/auth";
import { loadContent } from "./lib/content";
import { useLearn } from "./lib/state";
import { track } from "./lib/analytics";
import { ToastProvider, Spinner } from "./components/ui";
import { Layout } from "./components/Layout";
import { Login } from "./pages/Login";

// Each section is its own chunk: the first paint only loads the shell.
const Home = lazy(() => import("./pages/Home"));
const Onboarding = lazy(() => import("./pages/Onboarding"));
const Talk = lazy(() => import("./pages/talk/Talk"));
const VoiceCall = lazy(() => import("./pages/talk/VoiceCall"));
const Chat = lazy(() => import("./pages/chat/Chat"));
const Listen = lazy(() => import("./pages/listen/Listen"));
const Learn = lazy(() => import("./pages/learn/Learn"));
const Lesson = lazy(() => import("./pages/learn/Lesson"));
const Placement = lazy(() => import("./pages/learn/Placement"));
const Script = lazy(() => import("./pages/script/Script"));
const Words = lazy(() => import("./pages/words/Words"));
const Review = lazy(() => import("./pages/words/Review"));
const Pronounce = lazy(() => import("./pages/pronounce/Pronounce"));
const Bazaar = lazy(() => import("./pages/bazaar/Bazaar"));
const Pro = lazy(() => import("./pages/Pro"));
const Settings = lazy(() => import("./pages/Settings"));

export function App() {
  return (
    <BrowserRouter basename="/app">
      <ToastProvider>
        <AuthProvider>
          <Gate />
        </AuthProvider>
      </ToastProvider>
    </BrowserRouter>
  );
}

export function Loading({ full }: { full?: boolean }) {
  return (
    <div style={{ display: "grid", placeItems: "center", minHeight: full ? "100dvh" : "40vh" }}>
      <Spinner />
    </div>
  );
}

function Gate() {
  const { status } = useAuth();
  const [ready, setReady] = useState(false);
  const loc = useLocation();
  useEffect(() => {
    loadContent().then(() => setReady(true), () => setReady(true));
    track("app_open");
    // Time in the app = each visible stretch (like the apps' foreground→background),
    // never time the tab sat hidden.
    let started: number | null = Date.now();
    const onVisibility = () => {
      if (document.visibilityState === "visible") started ??= Date.now();
      else if (started !== null) {
        const seconds = Math.round((Date.now() - started) / 1000);
        started = null;
        if (seconds > 0) track("session_end", { seconds: Math.min(seconds, 4 * 3600) });
      }
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, []);

  if (status === "loading" || !ready) return <Loading full />;
  if (status === "signedOut") {
    return (
      <Routes>
        <Route path="/login" element={<Login />} />
        <Route path="*" element={<Navigate to={`/login${loc.pathname !== "/" ? `?next=${encodeURIComponent(loc.pathname + loc.search)}` : ""}`} replace />} />
      </Routes>
    );
  }
  return (
    <Suspense fallback={<Loading full />}>
      <Routes>
        <Route path="/login" element={<Navigate to="/" replace />} />
        <Route path="/welcome" element={<Onboarding />} />
        <Route element={<NeedsOnboarding><Layout /></NeedsOnboarding>}>
          <Route index element={<Page><Home /></Page>} />
          <Route path="talk" element={<Page><Talk /></Page>} />
          <Route path="talk/:scenario" element={<Page><VoiceCall /></Page>} />
          <Route path="chat" element={<Page><Chat /></Page>} />
          <Route path="listen" element={<Page><Listen /></Page>} />
          <Route path="learn" element={<Page><Learn /></Page>} />
          <Route path="learn/placement" element={<Page><Placement /></Page>} />
          <Route path="learn/:lessonId" element={<Page><Lesson /></Page>} />
          <Route path="script" element={<Page><Script /></Page>} />
          <Route path="words" element={<Page><Words /></Page>} />
          <Route path="words/review" element={<Page><Review /></Page>} />
          <Route path="pronounce" element={<Page><Pronounce /></Page>} />
          <Route path="bazaar" element={<Page><Bazaar /></Page>} />
          <Route path="pro" element={<Page><Pro /></Page>} />
          <Route path="settings" element={<Page><Settings /></Page>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Route>
      </Routes>
    </Suspense>
  );
}

function Page({ children }: { children: ReactNode }) {
  return <Suspense fallback={<Loading />}>{children}</Suspense>;
}

function NeedsOnboarding({ children }: { children: ReactNode }) {
  const onboarded = useLearn((s) => s.profile.onboarded);
  const loc = useLocation();
  // Paying from the onboarding flow (or a checkout return) shouldn't bounce.
  if (!onboarded && loc.pathname !== "/pro") return <Navigate to={`/welcome?next=${encodeURIComponent(loc.pathname + loc.search)}`} replace />;
  return <>{children}</>;
}
